import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProjectStore } from '../../../store/projectStore';
import { useTaskStore } from '../../../store/taskStore';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/Dialog';
import { TaskBoard } from '../tasks/TaskBoard';
import { GanttChart } from '../../../components/GanttChart';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';
import { Input } from '@/components/Input';
import { Textarea } from '../../../components/Textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/Select';
import { exportRows, type ExportColumn } from '../../../lib/export';

export function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const {
    currentProject,
    fetchProject,
    fetchAuditTrail,
    auditTrail,
    isLoading,
    removeProjectMember,
    addProjectMember
  } = useProjectStore();
  const { fetchTasks } = useTaskStore();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'board' | 'milestones' | 'members' | 'audit'>('overview');

  // Member Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Milestone Form State
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    name: '',
    description: '',
    dueDate: '',
    status: 'pending' as any,
    deliverables: '',
  });

  const { searchUsers, addMilestone, updateMilestone, deleteMilestone, updateProject } = useProjectStore();

  // Edit-project modal state
  const [isEditing, setIsEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', status: 'planning', description: '', healthStatus: 'green', budget: '' });

  const openEdit = () => {
    const p: any = currentProject;
    setEditForm({
      name: p.name ?? '',
      status: p.status ?? 'planning',
      description: p.description ?? '',
      healthStatus: p.healthStatus ?? p.health_status ?? 'green',
      budget: p.budget != null ? String(p.budget) : '',
    });
    setIsEditing(true);
  };

  const handleUpdateProject = async () => {
    if (!id) return;
    if (!editForm.name.trim()) { toast.error('Project name is required'); return; }
    setSavingEdit(true);
    try {
      await updateProject(id, {
        name: editForm.name.trim(),
        status: editForm.status,
        description: editForm.description.trim(),
        healthStatus: editForm.healthStatus,
        budget: editForm.budget ? Number(editForm.budget) : undefined,
      } as any);
      toast.success('Project updated');
      setIsEditing(false);
      fetchProject(id);
    } catch {
      toast.error('Failed to update project');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleExport = () => {
    const p: any = currentProject;
    const cols: ExportColumn<any>[] = [
      { header: 'Code', value: (r) => r.code },
      { header: 'Name', value: (r) => r.name },
      { header: 'Status', value: (r) => r.status },
      { header: 'Health', value: (r) => r.healthStatus ?? r.health_status ?? '' },
      { header: 'Budget', value: (r) => r.budget ?? '' },
      { header: 'Members', value: (r) => (r.members?.length ?? 0) },
    ];
    try {
      exportRows([p], cols, 'csv', `project-${p.code || 'export'}`);
    } catch {
      toast.error('Export failed');
    }
  };

  useEffect(() => {
    if (id) {
      fetchProject(id);
      fetchTasks(id);
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'audit') {
      fetchAuditTrail(id);
    }
  }, [id, activeTab]);

  if (isLoading || !currentProject) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs & Header */}
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold mb-2">
            <Link to="/work/projects" className="hover:text-primary">Projects</Link>
            <span>/</span>
            <span className="text-muted-foreground">{currentProject.code}</span>
          </div>
          <div className="flex items-center gap-4">
            <h1 className="tracking-tight text-foreground font-display text-page-title">{currentProject.name}</h1>
            <Badge variant={currentProject.status === 'active' ? 'success' : 'default'}>{currentProject.status}</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button variant="primary" onClick={openEdit}>Edit Project</Button>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Project Health', value: currentProject.healthStatus, type: 'health' },
          { label: 'Budget Utilized', value: `${((currentProject.progress || 0) * 0.8).toFixed(1)}%`, sub: `$${((currentProject.budget || 0) * (currentProject.progress || 0) / 100).toLocaleString()}` },
          { label: 'Team Members', value: currentProject.members.length, sub: 'Active resources' },
          { label: 'Timeline Progress', value: `${currentProject.progress}%`, type: 'progress' },
        ].map((stat, i) => (
          <div key={i} className="p-5 bg-card border border-border rounded-lg relative overflow-hidden group">
            <p className="text-xs text-muted-foreground font-semibold mb-1">{stat.label}</p>
            <div className="flex items-baseline gap-2">
              {stat.type === 'health' ? (
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-3 h-3 rounded-full ${stat.value === 'green' ? 'bg-success shadow-[0_0_8px_rgba(34,197,94,0.4)]' :
                    stat.value === 'yellow' ? 'bg-warning shadow-[0_0_8px_rgba(234,179,8,0.4)]' : 'bg-destructive'
                    }`} />
                  <span className="text-foreground capitalize font-display text-metric">{stat.value}</span>
                </div>
              ) : (
                <p className="text-foreground font-display text-metric">{stat.value}</p>
              )}
            </div>
            {stat.sub && <p className="text-xs text-muted-foreground font-semibold mt-1">{stat.sub}</p>}
            {stat.type === 'progress' && (
              <div className="h-1 w-full bg-card rounded-full mt-3 overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${currentProject.progress}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Main Content Tabs */}
      <div className="flex gap-4 border-b border-border sticky top-0 bg-background/80 z-10 pt-2">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'timeline', label: 'Timeline' },
          { id: 'board', label: 'Task Board' },
          { id: 'milestones', label: 'Milestones' },
          { id: 'members', label: 'Team' },
          { id: 'audit', label: 'Audit Trail' }
        ].map(t => (
          <Button variant="unstyled" size="unstyled"
            key={t.id}
            onClick={() => setActiveTab(t.id as any)}
            className={`pb-4 px-2 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground tracking-tight'
              }`}
          >
            {t.label}
            {activeTab === t.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </Button>
        ))}
      </div>

      {/* Tab Panels */}
      <div className="pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <h3 className="text-foreground mb-4 font-display text-card-title">Project Description</h3>
                <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap">
                  {currentProject.description || 'No description provided.'}
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-foreground font-display text-card-title">Tasks Summary</h3>
                  <button onClick={() => setActiveTab('board')} className="text-xs text-primary font-semibold hover:underline">View All →</button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-card rounded-lg border border-border text-center">
                    <p className="text-foreground font-display text-metric">{currentProject.tasksCount.total}</p>
                    <p className="text-xs text-muted-foreground font-semibold mt-1">Total Tasks</p>
                  </div>
                  <div className="p-4 bg-card rounded-lg border border-border text-center">
                    <p className="text-success font-display text-metric">{currentProject.tasksCount.completed}</p>
                    <p className="text-xs text-muted-foreground font-semibold mt-1">Completed</p>
                  </div>
                  <div className="p-4 bg-card rounded-lg border border-border text-center">
                    <p className="text-warning font-display text-metric">{currentProject.tasksCount.total - currentProject.tasksCount.completed}</p>
                    <p className="text-xs text-muted-foreground font-semibold mt-1">Remaining</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-foreground mb-4 font-display text-card-title">Project Details</h3>
                <dl className="space-y-4">
                  {[
                    { label: 'Client', value: currentProject.clientName || 'Internal' },
                    { label: 'Project Type', value: currentProject.type, capital: true },
                    { label: 'Industry', value: currentProject.industryTemplate || 'General', capital: true },
                    { label: 'Start Date', value: new Date(currentProject.startDate).toLocaleDateString() },
                    { label: 'Est. End Date', value: currentProject.endDate ? new Date(currentProject.endDate).toLocaleDateString() : 'N/A' },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-border/30 last:border-0">
                      <dt className="text-xs text-muted-foreground font-semibold">{item.label}</dt>
                      <dd className={`text-sm font-semibold text-foreground ${item.capital ? 'capitalize' : ''}`}>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </Card>

              <Card className="p-6">
                <h3 className="text-foreground mb-4 font-display text-card-title">Project Owner</h3>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary border border-primary/30 flex items-center justify-center font-semibold text-primary">
                    {currentProject.ownerName?.[0] || 'O'}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{currentProject.ownerName || 'Unknown Owner'}</p>
                    <p className="text-xs text-muted-foreground font-semibold">Delivery Manager</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-6">
            <GanttChart 
              tasks={currentProject.milestones.map(m => ({
                id: m.id,
                name: m.name,
                startDate: new Date(new Date(m.dueDate).getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                endDate: m.dueDate,
                progress: m.status === 'completed' ? 100 : m.status === 'in-progress' ? 50 : 0,
                status: m.status
              }))}
              projectStartDate={currentProject.startDate}
              projectEndDate={currentProject.endDate}
            />
          </div>
        )}

        {activeTab === 'board' && <TaskBoard isEmbedded projectId={id!} />}

        {activeTab === 'milestones' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center bg-card p-4 rounded-lg border border-border">
              <div>
                <h3 className="text-foreground font-display text-card-title">Project Roadmap</h3>
                <p className="text-xs text-muted-foreground">Track key phases and deliverables</p>
              </div>
              <Button variant="primary" onClick={() => setIsAddingMilestone(true)}>+ Add Milestone</Button>
            </div>

            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-card border-b border-border">
                  <tr className="text-sm font-semibold text-muted-foreground">
                    <th className="text-left px-6 py-4">Milestone Name</th>
                    <th className="text-left px-6 py-4">Due Date</th>
                    <th className="text-left px-6 py-4">Status</th>
                    <th className="text-left px-6 py-4">Deliverables</th>
                    <th className="text-right px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-12">
                  {currentProject.milestones.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground font-semibold bg-background/10">
                        No milestones defined yet.
                      </td>
                    </tr>
                  ) : (
                    currentProject.milestones.map(m => (
                      <tr key={m.id} className="hover:bg-card/30 transition-colors group">
                        <td className="px-6 py-5 font-semibold text-foreground">{m.name}</td>
                        <td className="px-6 py-5 text-muted-foreground">{new Date(m.dueDate).toLocaleDateString()}</td>
                        <td className="px-6 py-5">
                          <Badge variant={m.status === 'completed' ? 'success' : m.status === 'in-progress' ? 'warning' : 'default'}>
                            {m.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-wrap gap-2">
                            {m.deliverables.map((d, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-card border border-border rounded-lg text-muted-foreground font-semibold">
                                {d}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <Button variant="unstyled" size="unstyled"
                            onClick={() => deleteMilestone(currentProject.id, m.id)}
                            className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                                                      </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Card>

            <Dialog open={isAddingMilestone} onOpenChange={setIsAddingMilestone}>
              <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add Milestone</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-2">Name</label>
                      <Input
                        value={newMilestone.name}
                        onChange={e => setNewMilestone({ ...newMilestone, name: e.target.value })}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground"
                        placeholder="e.g. Beta Release"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-2">Due Date</label>
                      <Input
                        type="date"
                        value={newMilestone.dueDate}
                        onChange={e => setNewMilestone({ ...newMilestone, dueDate: e.target.value })}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-2">Deliverables (comma separated)</label>
                      <Input
                        value={newMilestone.deliverables}
                        onChange={e => setNewMilestone({ ...newMilestone, deliverables: e.target.value })}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground"
                        placeholder="e.g. Documentation, Mobile App"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="secondary" onClick={() => setIsAddingMilestone(false)}>Cancel</Button>
                      <Button variant="primary" onClick={async () => {
                        if (!newMilestone.name || !newMilestone.dueDate) {
                          toast.error('Please fill name and due date');
                          return;
                        }
                        try {
                          await addMilestone(currentProject.id, {
                            ...newMilestone,
                            deliverables: newMilestone.deliverables.split(',').map(s => s.trim()).filter(Boolean)
                          });
                          toast.success('Milestone added');
                          setIsAddingMilestone(false);
                          setNewMilestone({ name: '', description: '', dueDate: '', status: 'pending', deliverables: '' });
                        } catch (e) {
                          toast.error('Failed to add milestone');
                        }
                      }}>Add Milestone</Button>
                    </div>
                  </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === 'members' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentProject.members.map(member => (
              <Card key={member.id} className="p-6 flex flex-col gap-4 hover:border-primary/40 transition-all group relative">
                <Button variant="unstyled" size="unstyled"
                  onClick={() => removeProjectMember(currentProject.id, member.id)}
                  className="absolute top-4 right-4 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  title="Remove Resource"
                >
                  <span className="text-xs"></span>
                </Button>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg border border-border flex items-center justify-center text-xl shadow-lg ring-1 ring-white/5">
                    {member.avatar || member.name[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{member.name}</p>
                    <p className="text-xs text-muted-foreground font-semibold">{member.role}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-border/30">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-muted-foreground">Allocation</span>
                    <span className="text-xs font-semibold text-primary">{member.allocation}%</span>
                  </div>
                  <div className="h-1.5 bg-card rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full shadow-[0_0_8px_rgba(59,130,246,0.5)]" style={{ width: `${member.allocation}%` }} />
                  </div>
                </div>
              </Card>
            ))}
            <Card className="p-6 border-2 border-dashed border-border/50 flex flex-col items-center justify-center gap-4 cursor-default transition-all group min-h-[160px]">
              <div className="w-full space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (e.target.value.length >= 2) {
                        setIsSearching(true);
                        searchUsers(e.target.value).then(res => {
                          setSearchResults(res);
                          setIsSearching(false);
                        });
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-semibold"
                  />
                  {isSearching && <div className="animate-spin h-3 w-3 border-b-2 border-primary rounded-full" />}
                </div>

                {searchResults.length > 0 ? (
                  <div className="max-h-[120px] overflow-y-auto space-y-2 pr-2">
                    {searchResults.map(u => (
                      <div
                        key={u.id}
                        onClick={() => {
                          const alreadyMember = currentProject.members.some((m) => m.userId === u.id);
                          if (alreadyMember) {
                            toast.error(`${u.name} is already on the team.`);
                            return;
                          }
                          addProjectMember(currentProject.id, {
                            userId: u.id,
                            name: u.name,
                            role: 'member',
                            allocation: 100,
                            avatar: u.avatar_url,
                          });
                          toast.success(`Allocated ${u.name} to project.`);
                          setSearchTerm('');
                          setSearchResults([]);
                        }}
                        className="flex items-center gap-3 p-2 bg-background border border-border rounded-lg cursor-pointer hover:bg-primary/40 hover:border-primary/50 transition-all border-dashed"
                      >
                        <div className="w-7 h-7 rounded-lg bg-card border border-border flex items-center justify-center text-xs shrink-0 font-semibold">
                          {u.avatar_url ? <img src={u.avatar_url} className="w-full h-full rounded-lg" /> : u.name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground truncate">{u.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <span className="text-xs text-primary font-semibold">ALLOCATE</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-2">
                    <span className="text-xl opacity-20"></span>
                    <span className="text-xs font-semibold text-muted-foreground">Search by name or email</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-4 max-w-4xl">
            {auditTrail.length === 0 ? (
              <Card className="p-12 text-center text-muted-foreground font-semibold bg-background/20 border-dashed">
                No Audit Logs Available
              </Card>
            ) : (
              auditTrail.map((entry, idx) => (
                <div key={entry.id} className="relative flex gap-6 group">
                  {idx !== auditTrail.length - 1 && (
                    <div className="absolute left-[19px] top-10 bottom-0 w-px bg-border-12 group-hover:bg-primary/30 transition-colors" />
                  )}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 z-10 border transition-all ${entry.type === 'creation' ? 'bg-primary/50 border-primary text-primary' :
                    entry.type === 'milestone' ? 'bg-success/50 border-success text-success' :
                      entry.type === 'member' ? 'bg-warning/50 border-warning text-warning' :
                        'bg-card border-border text-muted-foreground'
                    }`}>
                    {entry.type === 'creation' ? '' : entry.type === 'milestone' ? '' : entry.type === 'member' ? '' : ''}
                  </div>
                  <div className="flex-1 pb-8">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-foreground group-hover:text-primary transition-colors text-sm font-semibold">{entry.action}</h4>
                      <span className="text-xs font-semibold text-muted-foreground tabular-nums">
                        {new Date(entry.timestamp).toLocaleDateString()} at {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-muted-foreground">{entry.actor}</span>
                      <span className="text-muted-foreground text-xs">•</span>
                      <Badge variant="default" className="text-xs h-4 leading-none py-0">{entry.module}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed bg-background/40 p-3 rounded-lg border border-border/50 max-w-2xl">
                      {entry.details}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Edit project modal */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Name</label>
              <Input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Status</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['planning', 'active', 'on-hold', 'completed', 'cancelled'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Health</label>
                <Select value={editForm.healthStatus} onValueChange={(v) => setEditForm(f => ({ ...f, healthStatus: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['green', 'amber', 'red'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Budget</label>
              <Input type="number" min={0} value={editForm.budget} onChange={(e) => setEditForm(f => ({ ...f, budget: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Description</label>
              <Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={savingEdit}>Cancel</Button>
            <Button variant="primary" onClick={handleUpdateProject} isLoading={savingEdit}>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
